import * as THREE from "three";
import { capturedPieceRestPosition, squareToWorldPosition } from "./board";
import { rankOf, squareAt } from "../game/board";
import { createPieceMesh } from "./pieces";
import { TweenManager, easeInOutCubic, easeOutBack } from "./animation";
import type { Board, Color, Move, Square } from "../game/types";

const MOVE_DURATION = 0.45;
const KNIGHT_HOP_HEIGHT = 0.75;
const PROMOTION_POP_DURATION = 0.35;
const INTRO_DROP_HEIGHT = 4.5;
const INTRO_DROP_DURATION = 0.5;
const INTRO_STAGGER_MS = 18;

// Choregraphie de capture : la piece gagnante bondit au-dessus de la case visee,
// frappe le sommet de la piece perdante (qui bascule au moment de l'impact), puis
// se pose. La piece perdante, une fois couchee (reaction au choc), se redresse en
// glissant jusqu'au rail hors plateau de son camp (cf. `capturedPieceRestPosition`)
// pour y reposer debout, en rang, plutot que couchee en vrac.
const STRIKE_RISE_DURATION = 0.24;
const STRIKE_RISE_HEIGHT = 1.15;
const STRIKE_DOWN_DURATION = 0.13;
const STRIKE_IMPACT_Y = 0.12;
const STRIKE_SETTLE_DURATION = 0.16;
const TOPPLE_DURATION = 0.22;
// Hauteur (au-dessus du plateau, pas de la table) a laquelle la piece perdante
// bascule juste apres l'impact, le temps qu'elle est encore sur sa case d'origine.
const TOPPLE_ARC_Y = 0.22;
const STAND_AND_SLIDE_DURATION = 0.42;

/** Gere les entites 3D des pieces (une par case occupee) et leurs animations de deplacement/capture,
 * chacune suivant la trajectoire propre a son type (ligne droite, saut du cavalier, roque simultane...). */
export class PieceLayer {
  private meshesBySquare = new Map<Square, THREE.Group>();
  private capturedCounts: Record<Color, number> = { w: 0, b: 0 };

  constructor(
    private readonly parent: THREE.Group,
    private readonly tweens: TweenManager,
  ) {}

  /** (Re)construit toutes les entites a partir d'un plateau, sans animation (etat initial / reinitialisation). */
  syncFromBoard(board: Board): void {
    for (const mesh of this.meshesBySquare.values()) {
      this.parent.remove(mesh);
    }
    this.meshesBySquare.clear();

    board.forEach((piece, square) => {
      if (!piece) return;
      const mesh = createPieceMesh(piece.type, piece.color);
      const pos = squareToWorldPosition(square);
      mesh.position.copy(pos);
      mesh.userData.square = square;
      this.parent.add(mesh);
      this.meshesBySquare.set(square, mesh);
    });
  }

  /**
   * Intro de partie : les pieces tombent en cascade jusqu'a leur case, comme
   * si une main invisible dressait le plateau — la scene est deja "vivante"
   * plutot que figee au moment ou le rideau noir se leve (meme esprit que le
   * coffret qui tombe deja dans l'intro de dkg-farkle).
   */
  async playIntro(board: Board): Promise<void> {
    for (const mesh of this.meshesBySquare.values()) {
      this.parent.remove(mesh);
    }
    this.meshesBySquare.clear();

    const drops: Promise<void>[] = [];
    let index = 0;

    board.forEach((piece, square) => {
      if (!piece) return;
      const mesh = createPieceMesh(piece.type, piece.color);
      const target = squareToWorldPosition(square);
      mesh.position.set(target.x, target.y + INTRO_DROP_HEIGHT, target.z);
      mesh.rotation.y = (Math.random() - 0.5) * 0.4;
      mesh.userData.square = square;
      this.parent.add(mesh);
      this.meshesBySquare.set(square, mesh);

      const delayMs = index * INTRO_STAGGER_MS;
      index++;
      const startRotation = mesh.rotation.y;
      drops.push(
        new Promise<void>((resolve) => {
          window.setTimeout(() => {
            this.tweens
              .animate(
                INTRO_DROP_DURATION,
                (t) => {
                  mesh.position.y = THREE.MathUtils.lerp(target.y + INTRO_DROP_HEIGHT, target.y, t);
                  mesh.rotation.y = startRotation * (1 - t);
                },
                easeInOutCubic,
              )
              .then(() => {
                mesh.position.copy(target);
                mesh.rotation.y = 0;
                resolve();
              });
          }, delayMs);
        }),
      );
    });

    await Promise.all(drops);
  }

  getMeshAt(square: Square): THREE.Group | undefined {
    return this.meshesBySquare.get(square);
  }

  /**
   * Piece perdante : bascule (poussee depuis le dessus, reaction au choc), puis se
   * redresse en glissant jusqu'au rail hors plateau de son camp, ou elle repose
   * debout, en rang (pas couchee en vrac). Quand `pushDirXZ` est fourni (capture
   * "de face", cf. `riseAndStrike`), la bascule suit l'axe reel de la poussee —
   * elle tombe en continuant la trajectoire de la piece qui la frappe, comme
   * repoussee a la main depuis le dessus — plutot qu'un axe fixe arbitraire. Sans
   * direction connue (prise en passant, pas de contact direct), elle bascule sur
   * un cote aleatoire.
   */
  private async toppleAndSlideToRail(square: Square, capturedColor: Color, pushDirXZ?: THREE.Vector2): Promise<void> {
    const mesh = this.meshesBySquare.get(square);
    if (!mesh) return;
    this.meshesBySquare.delete(square);
    delete mesh.userData.square;

    const index = this.capturedCounts[capturedColor]++;
    const restPosition = capturedPieceRestPosition(capturedColor, index);
    const startPosition = mesh.position.clone();

    // Axe de bascule horizontal, perpendiculaire a la direction de poussee : en
    // tournant autour de cet axe, le sommet de la piece se deplace exactement dans
    // le sens de la poussee (derivation : pour un axe unitaire (ax,0,az), le sommet
    // (0,h,0) se deplace selon axe x (0,h,0) = h*(-az, 0, ax) ; poser ax=dz, az=-dx
    // aligne donc ce deplacement sur la direction de poussee (dx,dz)).
    const dir = pushDirXZ && pushDirXZ.lengthSq() > 1e-6 ? pushDirXZ.clone().normalize() : null;
    const toppleAxis = dir
      ? new THREE.Vector3(dir.y, 0, -dir.x)
      : new THREE.Vector3(Math.cos(Math.random() * Math.PI * 2), 0, Math.sin(Math.random() * Math.PI * 2));
    const toppleAngle = Math.PI / 2;

    const toppleQuat = new THREE.Quaternion();
    await this.tweens.animate(TOPPLE_DURATION, (t) => {
      toppleQuat.setFromAxisAngle(toppleAxis, toppleAngle * t);
      mesh.quaternion.copy(toppleQuat);
      mesh.position.y = TOPPLE_ARC_Y * Math.sin(t * Math.PI * 0.5);
    }, easeInOutCubic);

    // Redressement + glisse jusqu'au rail : orientation interpolee (slerp) de la
    // bascule vers debout (leger devers final aleatoire, juste assez pour ne pas
    // avoir l'air d'un moule en serie, sans jamais redonner l'impression de vrac),
    // position interpolee du point de chute (encore au niveau du plateau) jusqu'au
    // rail (sur la table, donc nettement plus bas — cf. `capturedPieceRestPosition`).
    const fallenStart = new THREE.Vector3(startPosition.x, TOPPLE_ARC_Y, startPosition.z);
    const finalYaw = (Math.random() - 0.5) * 0.3;
    const uprightQuat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), finalYaw);
    await this.tweens.animate(STAND_AND_SLIDE_DURATION, (t) => {
      mesh.position.lerpVectors(fallenStart, restPosition, t);
      mesh.quaternion.slerpQuaternions(toppleQuat, uprightQuat, t);
    }, easeInOutCubic);
    mesh.position.copy(restPosition);
    mesh.quaternion.copy(uprightQuat);
  }

  /**
   * Vrai test de collision geometrique entre deux pieces (et non plus un minutage
   * fixe) : boites englobantes monde (`Box3`) recalculees depuis la geometrie et le
   * transform courant de chaque mesh, testees pour intersection. C'est ce test qui
   * determine l'instant exact du contact dans `riseAndStrike` ci-dessous.
   */
  private piecesCollide(a: THREE.Object3D, b: THREE.Object3D): boolean {
    const boxA = new THREE.Box3().setFromObject(a);
    const boxB = new THREE.Box3().setFromObject(b);
    return boxA.intersectsBox(boxB);
  }

  /**
   * Piece gagnante : bondit au-dessus de la case visee puis descend sur la piece
   * perdante, penchee dans l'axe reel du trajet (pas un axe fixe arbitraire) — comme
   * une main qui se penche depuis le dessus pour pousser la piece adverse, quelle
   * que soit la direction d'arrivee (ligne, diagonale, saut du cavalier). L'impact
   * (callback `onImpact`, qui declenche la bascule) n'est plus declenche a un
   * instant fixe de la choregraphie mais des que les volumes englobants des deux
   * pieces se recouvrent reellement (`piecesCollide`), teste a chaque frame de la
   * descente ; un filet de securite le declenche en fin de descente si, pour une
   * raison quelconque, le contact geometrique n'a jamais ete detecte (geometries
   * trop fines, cadence tres basse...), pour ne jamais bloquer la choregraphie.
   * Utilisee uniquement quand la case d'arrivee est la case de la piece capturee
   * (pas la prise en passant, ou les deux cases different).
   */
  private async riseAndStrike(mesh: THREE.Group, to: Square, pushDirXZ: THREE.Vector2, onImpact: () => void): Promise<void> {
    const from = mesh.position.clone();
    const target = squareToWorldPosition(to);
    const targetMesh = this.meshesBySquare.get(to);

    // Meme axe que la bascule de la piece capturee (cf. `toppleAndSlideToRail`) :
    // la piece gagnante penche "en avant" dans le sens ou elle pousse.
    const dir = pushDirXZ.lengthSq() > 1e-6 ? pushDirXZ.clone().normalize() : new THREE.Vector2(0, 1);
    const leanAxis = new THREE.Vector3(dir.y, 0, -dir.x);
    const leanQuat = new THREE.Quaternion();
    const baseQuat = mesh.quaternion.clone();

    await this.tweens.animate(STRIKE_RISE_DURATION, (t) => {
      mesh.position.x = THREE.MathUtils.lerp(from.x, target.x, t);
      mesh.position.z = THREE.MathUtils.lerp(from.z, target.z, t);
      mesh.position.y = STRIKE_RISE_HEIGHT * Math.sin(t * Math.PI * 0.5);
      leanQuat.setFromAxisAngle(leanAxis, -0.2 * Math.sin(t * Math.PI * 0.5));
      mesh.quaternion.multiplyQuaternions(leanQuat, baseQuat);
    }, easeInOutCubic);

    let impactFired = false;
    const fireImpact = () => {
      if (impactFired) return;
      impactFired = true;
      onImpact();
    };

    await this.tweens.animate(STRIKE_DOWN_DURATION, (t) => {
      const eased = t * t;
      mesh.position.y = THREE.MathUtils.lerp(STRIKE_RISE_HEIGHT, STRIKE_IMPACT_Y, eased);
      leanQuat.setFromAxisAngle(leanAxis, THREE.MathUtils.lerp(-0.2, 0.06, t));
      mesh.quaternion.multiplyQuaternions(leanQuat, baseQuat);
      if (!impactFired && targetMesh && this.piecesCollide(mesh, targetMesh)) {
        fireImpact();
      }
    });
    fireImpact();

    await this.tweens.animate(STRIKE_SETTLE_DURATION, (t) => {
      mesh.position.y = THREE.MathUtils.lerp(STRIKE_IMPACT_Y, 0, t);
      leanQuat.setFromAxisAngle(leanAxis, THREE.MathUtils.lerp(0.06, 0, t));
      mesh.quaternion.multiplyQuaternions(leanQuat, baseQuat);
    });
    mesh.position.copy(target);
    mesh.quaternion.copy(baseQuat);
  }

  private async animateStraightMove(mesh: THREE.Group, to: Square): Promise<void> {
    const from = mesh.position.clone();
    const target = squareToWorldPosition(to);
    await this.tweens.animate(MOVE_DURATION, (t) => {
      mesh.position.lerpVectors(from, target, t);
    }, easeInOutCubic);
    mesh.position.copy(target);
  }

  /** Trajectoire en arc (saut) pour le cavalier, afin de bien lire visuellement son deplacement en "L". */
  private async animateKnightMove(mesh: THREE.Group, to: Square): Promise<void> {
    const from = mesh.position.clone();
    const target = squareToWorldPosition(to);
    await this.tweens.animate(MOVE_DURATION, (t) => {
      mesh.position.lerpVectors(from, target, t);
      mesh.position.y = Math.sin(t * Math.PI) * KNIGHT_HOP_HEIGHT;
    }, easeInOutCubic);
    mesh.position.copy(target);
  }

  private async popIn(mesh: THREE.Group): Promise<void> {
    mesh.scale.setScalar(0.1);
    await this.tweens.animate(PROMOTION_POP_DURATION, (t) => {
      mesh.scale.setScalar(THREE.MathUtils.lerp(0.1, 1, easeOutBack(t)));
    });
    mesh.scale.setScalar(1);
  }

  /** Pose definitivement la piece gagnante sur sa case d'arrivee (avec promotion eventuelle), une fois le trajet anime termine. */
  private async resolveLanding(movingMesh: THREE.Group, move: Move): Promise<void> {
    if (move.promotion) {
      this.parent.remove(movingMesh);
      const promoted = createPieceMesh(move.promotion, move.piece.color);
      promoted.position.copy(squareToWorldPosition(move.to));
      promoted.userData.square = move.to;
      this.parent.add(promoted);
      this.meshesBySquare.set(move.to, promoted);
      await this.popIn(promoted);
    } else {
      movingMesh.userData.square = move.to;
      this.meshesBySquare.set(move.to, movingMesh);
    }
  }

  /** Anime un coup deja valide/applique cote moteur : capture, roque simultane, saut du cavalier, promotion. */
  async animateMove(move: Move): Promise<void> {
    const animations: Promise<void>[] = [];
    const movingMesh = this.meshesBySquare.get(move.from);
    if (movingMesh) this.meshesBySquare.delete(move.from);

    if (move.captured) {
      const captureSquare = move.enPassantCapture ?? move.to;
      const capturedColor = move.captured.color;

      if (movingMesh && captureSquare === move.to) {
        // Capture "de face" : la piece gagnante bondit sur la case visee et pousse la
        // piece perdante depuis le dessus, qui bascule pile au moment de l'impact dans
        // l'axe reel du trajet (from -> to). La bascule+glisse demarre a l'impact (pas
        // apres) mais reste suivie jusqu'au bout, pour que `animateMove` n'annonce pas
        // la fin trop tot.
        const fromPos = squareToWorldPosition(move.from);
        const toPos = squareToWorldPosition(move.to);
        const pushDirXZ = new THREE.Vector2(toPos.x - fromPos.x, toPos.z - fromPos.z);
        let topplePromise: Promise<void> = Promise.resolve();
        animations.push(
          this.riseAndStrike(movingMesh, move.to, pushDirXZ, () => {
            topplePromise = this.toppleAndSlideToRail(captureSquare, capturedColor, pushDirXZ);
          })
            .then(() => this.resolveLanding(movingMesh, move))
            .then(() => topplePromise),
        );
      } else {
        // Prise en passant : la case capturee (le pion adverse) differe de la case
        // d'arrivee (diagonale vide), pas de contact direct possible — la piece
        // perdante bascule et glisse vers son rail en parallele du coup normal.
        animations.push(this.toppleAndSlideToRail(captureSquare, capturedColor));
        if (movingMesh) {
          const isKnight = move.piece.type === "n";
          animations.push(
            (isKnight ? this.animateKnightMove(movingMesh, move.to) : this.animateStraightMove(movingMesh, move.to)).then(
              () => this.resolveLanding(movingMesh, move),
            ),
          );
        }
      }
    } else if (movingMesh) {
      const isKnight = move.piece.type === "n";
      animations.push(
        (isKnight ? this.animateKnightMove(movingMesh, move.to) : this.animateStraightMove(movingMesh, move.to)).then(
          () => this.resolveLanding(movingMesh, move),
        ),
      );
    }

    if (move.castle) {
      const rank = rankOf(move.from);
      const [rookFrom, rookTo] =
        move.castle === "k" ? [squareAt(7, rank), squareAt(5, rank)] : [squareAt(0, rank), squareAt(3, rank)];
      const rookMesh = this.meshesBySquare.get(rookFrom);
      if (rookMesh) {
        this.meshesBySquare.delete(rookFrom);
        animations.push(
          this.animateStraightMove(rookMesh, rookTo).then(() => {
            rookMesh.userData.square = rookTo;
            this.meshesBySquare.set(rookTo, rookMesh);
          }),
        );
      }
    }

    await Promise.all(animations);
  }
}
